import React, { useState } from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import TemplateText from 'src/components/TemplateText/TemplateText';
import MessageEventControl from '../MessageEventControl';
import {
  applyAppointmentSelection,
  filterPlaceholdersForAppointment,
  hasAppointmentPlaceholders,
  messageHasAppointment,
} from '../../helpers/appointmentMessageHelpers';
import { convertHtmlToText } from 'src/helpers/helpers';
import { MessageCreditEstimates } from 'src/components/MessageCreditEstimates';
import FileUploaderInput from 'src/components/FileUploaderInput/view';
import { AppointmentType, DefaultCustomField, FileLinkWithMimeType, MessageTemplate } from 'src/constants/types';
import { FormMessage } from '../../types';
import type { MessageTemplate as ConfigMessageTemplate } from '../../types';
import { resolveMessageFieldVisibility } from '../../helpers/messageVisibility';
import { CONFIRMATION_REMINDER_TEXT } from './messageFormText';
import { useIsEmbeddedBuilder } from '../../context';

interface MessageTextSectionProps {
  message: FormMessage;
  onChange: (next: Partial<FormMessage>) => void;
  allowedMessageTypes: string[];     // e.g. ['sms','mms']
  configMessageTemplate?: ConfigMessageTemplate;
  metadata: DefaultCustomField[];
  messageTemplates?: MessageTemplate[];
  position: number;
  optOutText?: string;
  isReminder?: boolean;
  isFlyer?: boolean;
  disabled?: boolean;
  /** Calendars available for per-message appointment attachment. */
  appointmentTypes?: AppointmentType[];
  appointmentsLoading?: boolean;
  onRequestRefreshAppointments?: () => void;
  onValidationChange?: (isValid: boolean) => void;
  /** Threaded down from MessagesField, the same way DataCaptureSection gets it. */
  companyId?: string;
}

export const MessageTextSection: React.FC<MessageTextSectionProps> = ({
  message,
  onChange,
  allowedMessageTypes,
  configMessageTemplate,
  metadata,
  messageTemplates = [],
  position,
  optOutText,
  isReminder = false,
  isFlyer = false,
  disabled = false,
  appointmentTypes = [],
  appointmentsLoading = false,
  onRequestRefreshAppointments,
  onValidationChange,
  companyId,
}) => {
  // Inside a dialog the in-editor picker would grow the dialog's scroll area instead of floating.
  const embedded = useIsEmbeddedBuilder();
  const [isMessageValid, setIsMessageValid] = useState(true);

  const { showTypeToggle, typeOptions, showAttachment, showSubject, showText } =
    resolveMessageFieldVisibility({
      messageType: message.type,
      template: configMessageTemplate,
      allowedMessageTypes,
      position,
      isReminder,
      isFlyer,
    });

  // Only a reminder attaches a calendar invite, and only it offers the
  // confirmation instructions in the editor.
  const showConfirmationText = isReminder && !!message.dataCapture;

  const handleLinksUpdate = (links: FileLinkWithMimeType[]) => {
    onChange({
      links: links.map(x => x.url),
      linksWithMimeType: links,
    });
  };

  // Message text for credit estimates: pos1 gets optOutText appended
  const textForEstimates = position === 1
    ? (message.text ?? '') + (optOutText ?? '')
    : (message.text ?? '');

  // Per-message appointment attachment ("Add Calendar Event"). Shown for every
  // non-reminder campaign — reminders take their appointment from the campaign
  // itself.
  const showAppointmentControl = !isReminder && !!companyId;
  const hasAppointment = messageHasAppointment({ appointmentId: message.appointmentId ?? undefined });
  const showDetachedPlaceholderError =
    !isReminder
    && !hasAppointment
    && hasAppointmentPlaceholders(convertHtmlToText(message.text || ''));

  // `companyId &&` is redundant with showAppointmentControl at runtime — it is
  // there so the control's required companyId narrows without a cast.
  const appointmentControl = showAppointmentControl && companyId && (
    <MessageEventControl
      appointments={[]}
      loading={appointmentsLoading}
      appointmentTypes={appointmentTypes}
      companyId={companyId}
      disabled={disabled}
      appointmentId={message.appointmentId ?? undefined}
      onChangeAppointment={appointmentId => onChange(applyAppointmentSelection(appointmentId))}
      onRequestRefreshAppointments={onRequestRefreshAppointments ?? (() => {})}
    />
  );

  return (
    <Stack direction="column" spacing={0}>
      {/* Toggle + appointment control share one row; the toggle's slot is held by
          a spacer when it is hidden so the calendar control stays right-aligned. */}
      {(showTypeToggle || showAppointmentControl) && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 1,
            marginBottom: '10px',
            width: '100%',
          }}
        >
          {showTypeToggle ? (
            <ToggleButtonGroup
              size="small"
              color="primary"
              disabled={disabled}
              value={message.type}
              exclusive
              onChange={(_, value) => {
                if (value) onChange({ type: value as FormMessage['type'] });
              }}
              aria-label="messageType"
            >
              {typeOptions.map((o) => (
                <ToggleButton key={o.value} value={o.value}>{o.title}</ToggleButton>
              ))}
            </ToggleButtonGroup>
          ) : (
            <span />
          )}
          {appointmentControl}
        </Box>
      )}

      {showAttachment && (
        <FileUploaderInput
          disabled={disabled}
          links={(message.linksWithMimeType ?? []) as FileLinkWithMimeType[]}
          updateLinks={handleLinksUpdate}
        />
      )}

      {/* Only a reminder attaches a calendar invite. */}
      {isReminder && (
        <FormGroup sx={{ marginTop: '-10px' }}>
          <FormControlLabel
            sx={{ marginRight: 0, marginBottom: '5px' }}
            control={
              <Checkbox
                disabled={disabled}
                name="attachCalendar"
                checked={!!message.attachCalendar}
                onChange={e => onChange({ attachCalendar: e.target.checked })}
              />
            }
            label="Attach calendar invite"
          />
        </FormGroup>
      )}

      {/* Subject field for MMS */}
      {showSubject && (
        <>
          <TextField
            fullWidth
            id="mms-subject"
            label="Subject (optional)"
            name="subject"
            variant="outlined"
            value={message.subject ?? ''}
            disabled={disabled}
            onChange={e => onChange({ subject: e.target.value })}
            style={{ marginTop: '20px' }}
          />
          <FormHelperText>
            Different devices and applications display the subject of an MMS in different ways.
            Avoid including critical information in the subject.
          </FormHelperText>
        </>
      )}

      {showText && (
        <Box sx={{ position: 'relative', marginBottom: '5px', pointerEvents: disabled ? 'none' : 'all' }}>
          <TemplateText
            floatingEmojiPicker={embedded}
            showConfirmationText={showConfirmationText}
            confirmationText={isReminder ? CONFIRMATION_REMINDER_TEXT : ''}
            setFocus={true}
            disabled={disabled}
            customFields={
              // Reminder campaigns always offer appointment placeholders (their appointment
              // comes from the campaign level); other campaigns only once this message has an
              // appointment attached.
              isReminder
                ? metadata
                : filterPlaceholdersForAppointment(metadata, hasAppointment) ?? metadata
            }
            messageTemplates={messageTemplates}
            text={message.text ?? ''}
            updateText={(newText) => onChange({ text: newText })}
            hasError={!isMessageValid}
            note={
              <MessageCreditEstimates
                viewType="campaign"
                messageType={message.type}
                messageText={textForEstimates}
                hasAttachments={!!(message.links?.length)}
                sx={{ py: 0.5, px: 1.25 }}
                onValidationChange={(valid) => {
                  setIsMessageValid(valid);
                  onValidationChange?.(valid);
                }}
              />
            }
          />
        </Box>
      )}

      {/* Warn when appointment.* merge tags are used with no appointment
          attached — they render empty at send time. */}
      {showDetachedPlaceholderError && (
        <FormHelperText error sx={{ marginBottom: '8px' }}>
          This message contains appointment placeholders but no appointment is attached. They
          will be empty when the message is sent. Attach an appointment or remove the placeholders.
        </FormHelperText>
      )}
    </Stack>
  );
};
